import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebase'; // Adjust path to your firebase config
import SharedContentViewer from './SharedContentViewer'; // The full viewer with Ask Preptune

export default function SharedContentPage() {
  const { shareId } = useParams();
  const [sharedContent, setSharedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (shareId) {
      loadSharedContent();
    }
  }, [shareId]);

  const loadSharedContent = async () => {
    try {
      console.log('📥 Loading shared content:', shareId);

      const sharedDocRef = doc(db, 'sharedContent', shareId);
      const docSnap = await getDoc(sharedDocRef);

      if (!docSnap.exists()) {
        console.error('❌ Content not found');
        setError('Content not found');
        setLoading(false);
        return;
      }

      const data = docSnap.data();
      
      console.log('📦 Loaded data:', {
        type: data.type,
        title: data.title,
        hasQuestions: !!(data.questions?.length),
        hasFlashcards: !!(data.flashcards?.length)
      });
      
      // Check if content is still active
      if (data.isActive === false) {
        setError('This content is no longer available');
        setLoading(false);
        return;
      }

      // Increment view count (async, don't block)
      incrementViewCount(shareId).catch(err => 
        console.warn('⚠️ View count error:', err)
      );

      // Format for viewer
      const formattedContent = {
        id: shareId,
        type: data.type,
        title: data.title,
        description: data.description || '',
        difficulty: data.difficulty || 'medium',
        questions: data.questions || [],
        flashcards: data.flashcards || [],
        createdBy: data.createdBy,
        createdAt: data.createdAt,
        viewCount: data.viewCount || 0
      };

      console.log('✅ Content ready:', {
        type: formattedContent.type,
        questionsCount: formattedContent.questions.length,
        flashcardsCount: formattedContent.flashcards.length
      });

      setSharedContent(formattedContent);
      setLoading(false);

    } catch (error) {
      console.error('❌ Failed to load:', error);
      setError('Failed to load content');
      setLoading(false);
    }
  };

  const incrementViewCount = async (shareId) => {
    try {
      const sharedDocRef = doc(db, 'sharedContent', shareId);
      await updateDoc(sharedDocRef, {
        viewCount: increment(1),
        lastAccessedAt: serverTimestamp()
      });
      console.log('📊 View count incremented');
    } catch (error) {
      console.warn('⚠️ Could not increment view count:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(170deg, #ecececf1 0%, #fefefedc 60%, #e0e7fcb2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '400px',
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #272727',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 0.8s linear infinite'
          }} />
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            Loading Content...
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6b7280'
          }}>
            Please wait while we load your shared content
          </p>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(170deg, #ecececf1 0%, #fefefedc 60%, #e0e7fcb2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '400px',
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            {error}
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '24px'
          }}>
            This link may be invalid or the content may have been removed.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '12px 24px',
              background: '#272727',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#3f3f3f'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#272727'}
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Success - show the viewer with Ask Preptune
  if (sharedContent) {
    return <SharedContentViewer sharedContent={sharedContent} />;
  }

  return null;
}